import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DatePicker } from 'antd';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import CustomSelect from '../custom-select/CustomSelect';
import { normalizeClientCabinetEmailDisplay } from '../../utils/normalizeClientCabinetEmailDisplay';
import './proxy-create-panel.css';

dayjs.locale('ru');

const parseKrakenIntListInput = (raw) => {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
  }
  return String(raw).trim().split(/[,;\s]+/).filter(Boolean)
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n));
};

/**
 * ProxyCreatePanel — функциональная форма создания прокси
 * По макету Figma 26-04 wa Proxy Manager (node 171-25252)
 *
 * Полностью заменяет старый Ant Design Modal.
 * Управляет собственным состоянием формы, принимает данные и колбэки из родителя.
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


// ==================== SUB-COMPONENTS ====================

/** Toggle switch row */
const ToggleRow = ({ label, checked = false, onChange = null }) => (
  <div className="pcp__toggle-row">
    <span className="pcp__toggle-label">{label}</span>
    <button
      type="button"
      className={`pcp__switch ${checked ? 'pcp__switch--on' : 'pcp__switch--off'}`}
      onClick={() => onChange && onChange(!checked)}
      aria-label={label}
      aria-checked={checked}
      role="switch"
      tabIndex={0}
    >
      <span className="pcp__switch-dot" />
    </button>
  </div>
);

// ==================== MAIN COMPONENT ====================

const ProxyCreatePanel = ({
  visible = false,
  onClose = null,
  onSubmit = null,
  submitting = false,

  // Data lists (from parent state)
  clients = [],
  tariffPlans = [],
  proxyTypes = [],
  availableCities = [],
  krakenConnections = [],
  filteredKrakenConnections = [],
  availableServers = [],
  filteredServers = [],
  availableOperators = [],
  osfpList = [],
  selectedTariffForCreate = null,

  // Cascading callbacks (parent handles filtering)
  onCityChange = null,
  onServerChange = null,
  onTariffChange = null,
  onRefreshTariffs = null,
  onRefreshOsfp = null,
  osfpRefreshLoading = false,
}) => {
  // ==================== FORM STATE ====================
  const [clientSearch, setClientSearch] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientFieldRef = useRef(null);
  const [tariffPlanName, setTariffPlanName] = useState('');
  const [expiresDate, setExpiresDate] = useState('');
  const [proxyQuantity, setProxyQuantity] = useState('1');
  const [proxyType, setProxyType] = useState('');
  const [city, setCity] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [serverId, setServerId] = useState('');
  const [mobileOperator, setMobileOperator] = useState([]);
  const [autoReplaceEnabled, setAutoReplaceEnabled] = useState(false);
  const [reconnectLinkEnabled, setReconnectLinkEnabled] = useState(false);
  const [isKeepalive, setIsKeepalive] = useState(false);
  const [isCompression, setIsCompression] = useState(false);
  const [krakenIsAllow, setKrakenIsAllow] = useState(true);
  const [krakenIsPorts, setKrakenIsPorts] = useState(true);
  const [krakenAllowInput, setKrakenAllowInput] = useState('');
  const [krakenTargetportInput, setKrakenTargetportInput] = useState('443');

  // Modem settings (shown when tariff has modem_settings_enabled)
  const [modemReconnectType, setModemReconnectType] = useState('');
  const [modemReconnectInterval, setModemReconnectInterval] = useState('');
  const [modemOsfpId, setModemOsfpId] = useState('');
  const [modemIsOsfp, setModemIsOsfp] = useState(false);

  // ==================== RESET ON OPEN ====================
  useEffect(() => {
    if (visible) {
      setClientSearch('');
      setClientDropdownOpen(false);
      setTariffPlanName('');
      setExpiresDate('');
      setProxyQuantity('1');
      setProxyType(proxyTypes[0] || 'http');
      setCity('');
      setConnectionId('');
      setServerId('');
      setMobileOperator([]);
      setAutoReplaceEnabled(false);
      setReconnectLinkEnabled(false);
      setIsKeepalive(false);
      setIsCompression(false);
      setKrakenIsAllow(true);
      setKrakenIsPorts(true);
      setKrakenAllowInput('');
      setKrakenTargetportInput('443');
      setModemReconnectType('');
      setModemReconnectInterval('');
      setModemOsfpId('');
      setModemIsOsfp(false);
    }
  }, [visible, proxyTypes]);

  // ==================== CLIENT SEARCH ====================

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return Array.isArray(clients) ? clients : [];
    const q = clientSearch.toLowerCase().trim();
    return (Array.isArray(clients) ? clients : []).filter(
      (c) => c.login.toLowerCase().includes(q) || (c.email && c.email.toLowerCase().includes(q))
    );
  }, [clients, clientSearch]);

  const isExistingClient = useMemo(() => {
    if (!clientSearch.trim()) return false;
    return (Array.isArray(clients) ? clients : []).some(
      (c) => c.login.toLowerCase() === clientSearch.toLowerCase().trim()
    );
  }, [clients, clientSearch]);

  const handleClientSelect = useCallback((login) => {
    setClientSearch(login);
    setClientDropdownOpen(false);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (clientFieldRef.current && !clientFieldRef.current.contains(e.target)) {
        setClientDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // ==================== CASCADING HANDLERS ====================

  const handleCityChange = useCallback((newCity) => {
    setCity(newCity);
    setConnectionId('');
    setServerId('');
    if (onCityChange) onCityChange(newCity);
  }, [onCityChange]);

  const handleConnectionChange = useCallback((newConnId) => {
    setConnectionId(newConnId);
    setServerId('');
    setMobileOperator([]); // Reset — operators will re-derive from the new connection's modems
    if (onServerChange) onServerChange(newConnId);
  }, [onServerChange]);

  const handleTariffChange = useCallback((newTariffName) => {
    setTariffPlanName(newTariffName);
    if (onTariffChange) onTariffChange(newTariffName);
  }, [onTariffChange]);

  // ==================== SUBMIT ====================

  const handleSubmit = useCallback(() => {
    if (!onSubmit) return;

    const trimmedClient = clientSearch.trim();
    const clientIsNew = !isExistingClient;

    const values = {
      client_mode: clientIsNew ? 'new' : 'existing',
      client_login: clientIsNew ? undefined : trimmedClient,
      new_client_login: clientIsNew ? trimmedClient : undefined,
      new_client_email: undefined,
      tariff_plan_name: tariffPlanName,
      expires_days: expiresDate ? Math.max(1, dayjs(expiresDate).diff(dayjs().startOf('day'), 'day')) : undefined,
      proxy_quantity: proxyQuantity === ''
        ? 1
        : (() => {
            const parsedQuantity = Number(proxyQuantity);
            if (Number.isNaN(parsedQuantity)) return 1;
            return Math.max(0, Math.min(1000, parsedQuantity));
          })(),
      proxy_type: proxyType,
      city: city || undefined,
      connection_id: connectionId || undefined,
      server_id: serverId || undefined,
      mobile_operator: mobileOperator.length > 0 ? mobileOperator : undefined,
      auto_replace_enabled: autoReplaceEnabled,
      reconnect_link_enabled: reconnectLinkEnabled,
      is_keepalive: isKeepalive,
      is_compression: isCompression,
      kraken_is_allow: krakenIsAllow,
      kraken_is_ports: krakenIsPorts,
      kraken_allow: parseKrakenIntListInput(krakenAllowInput),
      kraken_targetport: parseKrakenIntListInput(krakenTargetportInput),
    };

    // Modem settings (if tariff supports)
    if (selectedTariffForCreate && selectedTariffForCreate.modem_settings_enabled) {
      values.modem_reconnect_type = modemReconnectType || undefined;
      values.modem_reconnect_interval = modemReconnectInterval ? Number(modemReconnectInterval) : undefined;
      values.modem_osfp_id = modemOsfpId || undefined;
      values.modem_is_osfp = modemIsOsfp;
    }

    onSubmit(values);
  }, [
    clientSearch, isExistingClient,
    tariffPlanName, expiresDate, proxyQuantity, proxyType,
    city, connectionId, serverId, mobileOperator,
    autoReplaceEnabled, reconnectLinkEnabled, isKeepalive, isCompression,
    krakenIsAllow, krakenIsPorts, krakenAllowInput, krakenTargetportInput,
    selectedTariffForCreate, modemReconnectType, modemReconnectInterval, modemOsfpId, modemIsOsfp,
    onSubmit,
  ]);

  // ==================== CLOSE ====================

  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  // ==================== DERIVED LISTS ====================

  // Determine which lists to show based on current selection, NOT array length
  const connectionsToShow = city ? filteredKrakenConnections : krakenConnections;
  const serversToShow = connectionId ? filteredServers : availableServers;

  // Derive operators from visible modems when a connection is selected
  const operatorsToShow = useMemo(() => {
    if (!connectionId) return availableOperators;
    const fromModems = [...new Set(
      serversToShow
        .map((s) => s.mobile_operator)
        .filter(Boolean)
    )];
    return fromModems.length > 0 ? fromModems : availableOperators;
  }, [connectionId, serversToShow, availableOperators]);

  // ==================== EARLY RETURN ====================

  if (!visible) return null;

  return createPortal(
    <div
      className="pcp-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="pcp" role="dialog" aria-label="Создание прокси">

        {/* ==================== HEADER ==================== */}
        <div className="pcp__header">
          <h2 className="pcp__title">создание прокси</h2>
          <button
            type="button"
            className="pcp__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ==================== BODY ==================== */}
        <div className="pcp__body">

          {/* ---- Section: Информация о клиенте ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Информация о клиенте</h3>
            <div className="pcp__row">
              <div className="pcp__field pcp__field--client" ref={clientFieldRef}>
                <div className="pcp__label">
                  <span className="pcp__label-required">*</span>
                  <span className="pcp__label-text">Клиент</span>
                </div>
                <div className="pcp__autocomplete">
                  <input
                    type="text"
                    className="pcp__text-input"
                    placeholder="Введите логин клиента"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setClientDropdownOpen(true);
                    }}
                    onFocus={() => setClientDropdownOpen(true)}
                    aria-label="Поиск клиента"
                    aria-autocomplete="list"
                    autoComplete="off"
                  />
                  {clientSearch.trim() && (
                    <span className={`pcp__client-badge ${isExistingClient ? 'pcp__client-badge--existing' : 'pcp__client-badge--new'}`}>
                      {isExistingClient ? 'Существующий' : 'Новый'}
                    </span>
                  )}
                  {clientDropdownOpen && (
                    <ul className="pcp__autocomplete-list" role="listbox">
                      {filteredClients.length > 0 ? (
                        filteredClients.map((c) => (
                          <li
                            key={c.login}
                            className="pcp__autocomplete-item"
                            role="option"
                            tabIndex={0}
                            aria-selected={clientSearch === c.login}
                            onClick={() => handleClientSelect(c.login)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') handleClientSelect(c.login);
                            }}
                          >
                            <span className="pcp__autocomplete-login">{c.login}</span>
                            {c.email && (
                              <span className="pcp__autocomplete-email">{normalizeClientCabinetEmailDisplay(c.email)}</span>
                            )}
                          </li>
                        ))
                      ) : clientSearch.trim() ? (
                        <li className="pcp__autocomplete-item pcp__autocomplete-item--empty">
                          Клиент не найден — будет создан автоматически
                        </li>
                      ) : null}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ---- Section: Тариф и лимиты ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Тариф и лимиты</h3>

            <div className="pcp__field">
              <div className="pcp__label">
                <span className="pcp__label-required">*</span>
                <span className="pcp__label-text">Тарифный план</span>
                <button
                  type="button"
                  className="pcp__label-action"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onRefreshTariffs) onRefreshTariffs();
                  }}
                  tabIndex={0}
                >
                  Обновить
                </button>
              </div>
              <CustomSelect
                value={tariffPlanName}
                onChange={handleTariffChange}
                placeholder={`Выберите тариф (${tariffPlans.length} доступно)`}
                options={[
                  { value: '', label: `Выберите тариф (${tariffPlans.length} доступно)` },
                  ...(Array.isArray(tariffPlans) ? tariffPlans.map((plan) => ({ value: plan.name, label: plan.name })) : []),
                ]}
                searchable
              />
            </div>

            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-required">*</span>
                  <span className="pcp__label-text">Количество прокси</span>
                </div>
                <input
                  type="number"
                  className="pcp__text-input"
                  value={proxyQuantity}
                  min={0}
                  max={1000}
                  placeholder="1"
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === '') {
                      setProxyQuantity('');
                      return;
                    }

                    const parsedValue = Number(rawValue);
                    if (Number.isNaN(parsedValue)) {
                      setProxyQuantity('');
                      return;
                    }

                    setProxyQuantity(String(Math.max(0, Math.min(1000, parsedValue))));
                  }}
                />
              </div>
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Тип прокси</span>
                </div>
                <CustomSelect
                  value={proxyType}
                  onChange={setProxyType}
                  placeholder="Тип прокси"
                  options={proxyTypes.map((t) => ({ value: t, label: t.toUpperCase() }))}
                />
              </div>
              <div className="pcp__field pcp__field--full-mobile pcp__field--expires">
                <div className="pcp__label">
                  <span className="pcp__label-text">Срок действия</span>
                </div>
                <div className="pcp__expires-wrap">
                  <DatePicker
                    value={expiresDate ? dayjs(expiresDate) : null}
                    onChange={(date) => {
                      if (date) {
                        setExpiresDate(date.format('YYYY-MM-DD'));
                      } else {
                        setExpiresDate('');
                      }
                    }}
                    format="DD.MM.YYYY"
                    placeholder="Безлимит"
                    style={{ width: '100%' }}
                    allowClear
                    getPopupContainer={(trigger) => trigger.parentElement}
                    renderExtraFooter={() => (
                      <div className="pcp__expires-footer">
                        {expiresDate && (() => {
                          const now = dayjs().startOf('day');
                          const exp = dayjs(expiresDate).startOf('day');
                          const days = exp.diff(now, 'day');
                          const isExpired = days < 0;
                          const isSoon = days >= 0 && days <= 3;
                          let label = '';
                          if (days < 0) label = `Просрочено на ${Math.abs(days)} дн.`;
                          else if (days === 0) label = 'Истекает сегодня';
                          else if (days === 1) label = 'Остался 1 день';
                          else if (days >= 2 && days <= 4) label = `Осталось ${days} дня`;
                          else label = `Осталось ${days} дней`;
                          return (
                            <span className={`pcp__expires-days-hint ${isExpired ? 'pcp__expires-days-hint--expired' : ''} ${isSoon ? 'pcp__expires-days-hint--soon' : ''}`}>
                              {label}
                            </span>
                          );
                        })()}
                        <div className="pcp__expires-presets">
                          {[1, 30, 60, 90, 180, 365].map((d) => {
                            const target = dayjs().add(d, 'day');
                            const iso = target.format('YYYY-MM-DD');
                            return (
                              <button
                                key={d}
                                type="button"
                                className={`pcp__expires-preset-btn ${expiresDate === iso ? 'pcp__expires-preset-btn--active' : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setExpiresDate(iso)}
                              >
                                +{d}д
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ---- Section: Размещение ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Размещение</h3>

            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Город</span>
                </div>
                <CustomSelect
                  value={city}
                  onChange={handleCityChange}
                  placeholder={`Выберите город (${availableCities.length})`}
                  options={[
                    { value: '', label: `Выберите город (${availableCities.length})` },
                    ...availableCities.map((c) => ({ value: c, label: c })),
                  ]}
                  searchable
                />
              </div>
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Сервер Kraken</span>
                </div>
                <CustomSelect
                  value={connectionId}
                  onChange={handleConnectionChange}
                  placeholder={`Выберите сервер (${connectionsToShow.length})`}
                  options={[
                    { value: '', label: `Выберите сервер (${connectionsToShow.length})` },
                    ...connectionsToShow.map((conn) => ({ value: String(conn.id), label: `${conn.name} (${conn.server_url})` })),
                  ]}
                  searchable
                />
              </div>
            </div>

            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Модем</span>
                </div>
                <CustomSelect
                  value={serverId}
                  onChange={setServerId}
                  placeholder={`Выберите модем (${serversToShow.length})`}
                  options={[
                    { value: '', label: `Выберите модем (${serversToShow.length})` },
                    ...serversToShow.map((s) => { const octet = (s.ip_address || s.ip || '').split('.')[2]; return { value: String(s.id), label: octet ? `№ ${octet} ${s.name || s.ip}` : (s.name || s.ip) }; }),
                  ]}
                  searchable
                />
              </div>
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Оператор</span>
                </div>
                <CustomSelect
                  value={mobileOperator[0] || ''}
                  onChange={(val) => setMobileOperator(val ? [val] : [])}
                  placeholder={`Выберите оператора (${operatorsToShow.length})`}
                  options={[
                    { value: '', label: `Выберите оператора (${operatorsToShow.length})` },
                    ...operatorsToShow.map((op) => ({ value: op, label: op })),
                  ]}
                />
              </div>
            </div>

            <div className="pcp__info-box">
              <p className="pcp__info-box-text">
                При создании нескольких прокси они будут распределены случайным образом между выбранными операторами. (Доступно: {operatorsToShow.length})
              </p>
            </div>
          </div>

          {/* ---- Section: Параметры ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Параметры</h3>
            <ToggleRow label="Автозамена устройства при недоступности прокси" checked={autoReplaceEnabled} onChange={setAutoReplaceEnabled} />
            <ToggleRow label="Ссылка смены IP" checked={reconnectLinkEnabled} onChange={setReconnectLinkEnabled} />
            {proxyType !== 'socks5' && (
              <>
                <ToggleRow label="HTTP Keep-Alive соединения" checked={isKeepalive} onChange={setIsKeepalive} />
                <ToggleRow label="Сжатие HTTP-ответов (gzip)" checked={isCompression} onChange={setIsCompression} />
              </>
            )}
          </div>

          <div className="pcp__section">
            <h3 className="pcp__section-title">Разрешения прокси</h3>
            <ToggleRow label="Доступ прокси по списку ресурсов" checked={krakenIsAllow} onChange={setKrakenIsAllow} />
            <ToggleRow label="Доступ к портам из списка" checked={krakenIsPorts} onChange={setKrakenIsPorts} />
            <div className="pcp__field">
              <div className="pcp__label">
                <span className="pcp__label-text">Разрешенные ресурсы по ID</span>
              </div>
              <input
                type="text"
                className="pcp__text-input"
                placeholder="например: 1, 2, 5"
                value={krakenAllowInput}
                onChange={(e) => setKrakenAllowInput(e.target.value)}
                aria-label="Разрешенные ресурсы по ID, через запятую"
                autoComplete="off"
              />
            </div>
            <div className="pcp__field">
              <div className="pcp__label">
                <span className="pcp__label-text">Разрешенные порты</span>
              </div>
              <input
                type="text"
                className="pcp__text-input"
                placeholder="по умолчанию 443; несколько через запятую"
                value={krakenTargetportInput}
                onChange={(e) => setKrakenTargetportInput(e.target.value)}
                aria-label="Разрешенные порты, через запятую"
                autoComplete="off"
              />
            </div>
          </div>

          {/* ---- Section: Настройки модема (только если тариф поддерживает) ---- */}
          {selectedTariffForCreate && selectedTariffForCreate.modem_settings_enabled && (
            <div className="pcp__section">
              <h3 className="pcp__section-title">Настройки модема</h3>
              <div className="pcp__info-box" style={{ background: '#ecf1fe' }}>
                <p className="pcp__info-box-text" style={{ color: '#5283f4' }}>
                  Сохраняются в прокси и применяются при переподключении/автозамене
                </p>
              </div>

              <div className="pcp__row">
                <div className="pcp__field">
                  <div className="pcp__label">
                    <span className="pcp__label-text">Тип переподключения</span>
                  </div>
                  <CustomSelect
                    value={modemReconnectType}
                    onChange={setModemReconnectType}
                    placeholder="Выберите тип"
                    options={[
                      { value: '', label: 'Выберите тип' },
                      { value: 'time', label: 'По времени' },
                      { value: 'link', label: 'Ссылка' },
                      { value: 'time_link', label: 'Время + ссылка' },
                    ]}
                  />
                </div>
                {modemReconnectType !== 'link' && (
                  <div className="pcp__field">
                    <div className="pcp__label">
                      <span className="pcp__label-text">Интервал (сек)</span>
                    </div>
                    <input
                      type="number"
                      className="pcp__text-input"
                      placeholder="240"
                      value={modemReconnectInterval}
                      onChange={(e) => setModemReconnectInterval(e.target.value)}
                      min={1}
                      max={86400}
                    />
                  </div>
                )}
              </div>

              <div className="pcp__row">
                <div className="pcp__field">
                  <div className="pcp__label">
                    <span className="pcp__label-text">TCP отпечаток</span>
                    <button
                      type="button"
                      className="pcp__label-action"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onRefreshOsfp) onRefreshOsfp();
                      }}
                      disabled={osfpRefreshLoading}
                      aria-label="Обновить TCP отпечаток"
                      tabIndex={0}
                    >
                      {osfpRefreshLoading ? 'Обновление...' : 'Обновить'}
                    </button>
                  </div>
                  <CustomSelect
                    value={modemOsfpId}
                    onChange={(val) => {
                      setModemOsfpId(val);
                      if (val) setModemIsOsfp(true);
                    }}
                    placeholder="Выберите отпечаток"
                    options={[
                      { value: '', label: 'Выберите отпечаток' },
                      ...osfpList.map((o) => ({ value: String(o.id), label: o.name || `ID: ${o.id}` })),
                    ]}
                  />
                </div>
                <div className="pcp__field">
                  <ToggleRow label="Включить TCP отпечаток" checked={modemIsOsfp} onChange={setModemIsOsfp} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ==================== FOOTER ==================== */}
        <div className="pcp__footer">
          <button
            type="button"
            className="pcp__btn pcp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Отмена"
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="pcp__btn pcp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-label="Создать"
            tabIndex={0}
          >
            {submitting ? 'Создание...' : 'Создать'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProxyCreatePanel;
